import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import AnnouncementIcon from '@mui/icons-material/Announcement'
import { useAuthContext } from 'AuthProvider'
import { type CellValueChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useCenterStockItem } from 'hooks/useCenterStockItem'
import { useGridEdit } from 'hooks/useGridEdit'
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { convertDateToRequestParam } from 'utils/convertFromLocalDate'
import { validateRequired } from 'utils/validation'

import type {
  ColDef,
  ColGroupDef,
  IRichCellEditorParams,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-enterprise'
import type {
  MstCenterStockItem,
  CenterStockItem,
  GetMstCenterStockItemCenterStockItemGetRequest,
  MstItem,
  MstSupplier,
  Groups,
  MstCenter,
  Codes,
} from 'api-client'
import type { StockCenterItemAlertDialogEdit } from 'components/dialog/StockCenterItemAlertDialog/StockCenterItemAlertDialog'
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
  org_center_id: 'orgCenterId',
  org_item_code: 'orgItemCode',
  start_date: 'startDate',
  stock_days_setting: 'stockDaysSetting',
  safety_stock_setting: 'safetyStockSetting',
  min_stock_quantity: 'minStockQuantity',
  stop_ship: 'stopShip',
  stop_arrival: 'stopArrival',
  org_supplier_id: 'orgSupplierId',
  order_unit_type: 'orderUnitType',
  case_lot: 'caseLot',
  min_order_quantity: 'minOrderQuantity',
  remarks: 'remarks',
  delete_flg: 'deleteFlg',
  sales_end_alert_date: 'salesEndAlertDate',
  is_muted: 'isMuted',
  is_discontinued: 'isDiscontinued',
}

const primayKey = ['orgCenterId', 'orgItemCode']

interface DataListProps extends DataListPropsBase {
  list: CenterStockItem
  items: MstItem[]
  supplier: MstSupplier[]
  systype: Groups[]
  center: MstCenter[]
  searchParam: GetMstCenterStockItemCenterStockItemGetRequest
  fixedGridHeight?: string
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
  dialogEdits?: StockCenterItemAlertDialogEdit[]
  focusTarget?: { orgCenterId: string; orgItemCode: string }
  onFocusHandled?: () => void
}

interface MstCenterStockItemWithId extends MstCenterStockItem, RecordEditEx {}

const trimPk = (value: string | null | undefined) => String(value ?? '').trim()

function syncDialogEditsToAgGrid(
  gridRef: React.RefObject<AgGridReact | null | undefined>,
  adds: MstCenterStockItemWithId[],
  updates: MstCenterStockItemWithId[]
) {
  if (adds.length === 0 && updates.length === 0) return
  requestAnimationFrame(() => {
    const gridApi = gridRef.current?.api
    if (!gridApi || gridApi.isDestroyed()) return
    gridApi.applyTransaction({ add: adds, update: updates })
    gridApi.refreshCells({ force: true })
  })
}

function applyCenterStockDialogEdits(
  prev: MstCenterStockItemWithId[],
  dialogEdits: StockCenterItemAlertDialogEdit[],
  createBlankRow: () => MstCenterStockItemWithId,
  setMstItemCol: (row: MstCenterStockItemWithId, orgItemCode: string | null | undefined) => void
): { next: MstCenterStockItemWithId[]; adds: MstCenterStockItemWithId[]; updates: MstCenterStockItemWithId[] } {
  const next = [...prev]
  const adds: MstCenterStockItemWithId[] = []
  const updates: MstCenterStockItemWithId[] = []

  dialogEdits.forEach((edit) => {
    const editCenter = trimPk(edit.orgCenterId)
    const editItem = trimPk(edit.orgItemCode)
    if (!editCenter || !editItem) return

    let idx = next.findIndex(
      (row) => trimPk(row.orgCenterId) === editCenter && trimPk(row.orgItemCode) === editItem
    )

    let needsBlankRow = false
    let target: MstCenterStockItemWithId | undefined

    if (idx >= 0) {
      target = next[idx]
      if (edit.masterRegister && !target.isNewRecord && target.insertDatetime != null) {
        idx = -1
        target = undefined
      }
    }

    if (idx < 0 && (edit.masterRegister || edit.isDiscontinued || edit.isMuted)) {
      needsBlankRow = true
      target = {
        ...createBlankRow(),
        orgCenterId: edit.orgCenterId,
        orgItemCode: edit.orgItemCode,
        startDate: edit.minRecDate ?? null,
      }
      setMstItemCol(target, edit.orgItemCode)
      idx = next.length
      next.push(target)
    }

    if (!target || idx < 0) return

    const editedFields = new Set(target.editedFields)
    if (needsBlankRow) {
      editedFields.add('orgCenterId')
      editedFields.add('orgItemCode')
      editedFields.add('startDate')
    }
    if (edit.masterRegister) editedFields.add('action:マスタ追加')
    if (edit.isDiscontinued) {
      editedFields.add('isDiscontinued')
      editedFields.add('action:取扱終了')
    }
    if (edit.isMuted) {
      editedFields.add('isMuted')
      editedFields.add('action:通知不要')
    }

    const updated: MstCenterStockItemWithId = {
      ...target,
      isEdited: true,
      editedFields,
      ...(edit.isDiscontinued ? { isDiscontinued: true } : {}),
      ...(edit.isMuted ? { isMuted: true } : {}),
    }
    next[idx] = updated
    if (needsBlankRow) adds.push(updated)
    else updates.push(updated)
  })

  return { next, adds, updates }
}

function DataList({
  list,
  isLoading,
  items,
  supplier,
  center,
  systype,
  searchParam,
  fixedGridHeight,
  onReload,
  updateTrigger,
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
  dialogEdits = [],
  focusTarget,
  onFocusHandled,
}: DataListProps) {
  const { loginUserInfo } = useAuthContext()
  const { result, fetchCenterStockItemUpdate, isUpdating, updateResult } =
    useCenterStockItem()
  const internalGridRef = useRef<AgGridReact<MstCenterStockItemWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  
  const containerStyle = useMemo(() => {
    if (fixedGridHeight) return { width: '100%', height: fixedGridHeight }
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible, fixedGridHeight])
  
  const gridStyle = useMemo(() => {
    if (fixedGridHeight) return { width: '100%', height: fixedGridHeight }
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible, fixedGridHeight])
  const [editorDataReady, setEditorDataReady] = useState(false)
  const [row, setRow] = useState<MstCenterStockItemWithId[]>(
    list.list.map((item) => ({ ...item, ...initRecordEditEx() }))
  )
  const [supplierSelect, setSupplierSelect] = useState<(string | null)[]>([])
  const [itemSelect, setItemSelect] = useState<(string | null)[]>([])
  const [centerSelect, setCenterSelect] = useState<(string | null)[]>([])
  const [orderSysTypeCodeLists, setOrderSysTypeCodeLists] = useState<{ [key: string]: Codes[] }>({})
  const [orderSysTypeCodeSelects, setOrderSysTypeCodeSelects] = useState<{ [key: string]: (string | null)[] }>({})
  const orderUnitTypeGroups = ['order_unit_type']
  const [rowChangeTriggerRowID, setRowChangeTriggerRowID] = useState<string | null>(null);
  
  const tableFieldToGroup: { [key: string]: string } = {
    orderUnitType: 'order_unit_type',
  }

  useEffect(() => {
    setResultMessage(result)
  }, [result])
  useEffect(() => {
    // setResultMessage({...updateResult} as ResponseResult | ResponseUpdate)
  }, [updateResult])

  useEffect(() => {
    const allSelected = orderUnitTypeGroups.every((group) => orderSysTypeCodeSelects[group]?.length > 0)
    if (supplierSelect.length > 0 && itemSelect.length > 0 && centerSelect.length > 0 && allSelected) {
      setEditorDataReady(true)
    }
  }, [supplierSelect, itemSelect, centerSelect, orderSysTypeCodeSelects])

  useEffect(() => {
    const values = supplier?.map((r) => r.orgSupplierId) || []
    setSupplierSelect(values)
  }, [supplier])
  useEffect(() => {
    const values = center?.map((r) => r.orgCenterId) || []
    setCenterSelect(values)
  }, [center])
  useEffect(() => {
    const newLists: { [key: string]: Codes[] } = {}
    const newSelects: { [key: string]: (string | null)[] } = {}

    orderUnitTypeGroups.forEach((group) => {
      const codes = systype.find((r) => r.typeGroup === group)?.codes || []
      newLists[group] = codes
      newSelects[group] = codes.map((r) => r.typeCode)
    })

    setOrderSysTypeCodeLists(newLists)
    setOrderSysTypeCodeSelects(newSelects)
  }, [systype])
  useEffect(() => {
    // console.log("items", items)
    // if (items.length === 0) return
    setEditorDataReady(false)
    const values = items?.map((r) => r.orgItemCode) || []
    setItemSelect([...values])
  }, [items])

  const createBlankRow = useCallback((): MstCenterStockItemWithId => {
    // カスタムフックから直接コンポーネントの状態にアクセスすることはできないため、useGridEditからコールされたcreateBlankRowの場合、searchParamが取れない
    // rowChangeTriggerRowIDのEffectで処理する必要がある
    const searchCenterDefault = center.find((rowCenter) => rowCenter.centerId === searchParam.centerId)
    const blankRow: Partial<MstCenterStockItemWithId> = {
      companyId: loginUserInfo?.companyId || null,
      orgItemCode: searchParam.orgItemCode || null,
      orgCenterId: searchCenterDefault?.orgCenterId || null,
      centerId: searchParam.centerId || null,
      supplierCompanyId: null,
      startDate: null,
      stockDaysSetting: null,
      safetyStockSetting: null,
      minStockQuantity: null,
      orderUnitType: null,
      caseLot: null,
      minOrderQuantity: null,
      stopShip: false,
      stopArrival: false,
      orgSupplierId: null,
      itemCode: null,
      recencyFlg: false,
      deleteDatetime: null,
      updateUser: null,
      deleteUser: null,
      insertDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      updateDatetime: null,
      remarks: null,
      deleteFlg: false,
      salesEndAlertDate: null,
      isMuted: false,
      isDiscontinued: false,
      isAlert: null,
      ...initRecordEditEx(true),
    }
    setMstItemCol(blankRow as MstCenterStockItemWithId, searchParam.orgItemCode)
    return blankRow as MstCenterStockItemWithId
  }, [searchParam, loginUserInfo?.companyId, loginUserInfo?.userId])

  const setMstItemCol = useCallback((pRow: MstCenterStockItemWithId, orgItemCode: string | null | undefined) => {
    if (pRow) {
      const selectedItem = { ...items.find((orgId) => orgId.orgItemCode === orgItemCode) }
      // console.log("setMstItemCol", pRow, orgItemCode, items.length, selectedItem)
      Object.assign(pRow, {
        mstItemItemName: selectedItem ? selectedItem.itemName : null,
        mstItemSalesStartdate: selectedItem ? selectedItem.salesStartdate : null,
        mstItemSalesEnddate: selectedItem ? selectedItem.salesEnddate : null,
        mstItemLongside: selectedItem ? selectedItem.longside : null,
        mstItemShortside: selectedItem ? selectedItem.shortside : null,
        mstItemHeight: selectedItem ? selectedItem.height : null,
        mstItemWeight: selectedItem ? selectedItem.weight : null,
        mstItemSpec: selectedItem ? selectedItem.spec : null,
        mstItemMaxPalletQuantity: selectedItem ? selectedItem.maxPalletQuantity : null,
        mstItemPalletQuantityFace: selectedItem ? selectedItem.palletQuantityFace : null,
        mstItemPalletQuantityTier: selectedItem ? selectedItem.palletQuantityTier : null,
        mstItemJan: selectedItem ? selectedItem.jan : null,
        mstItemJanCase: selectedItem ? selectedItem.janCase : null,
        mstItemGtin13: selectedItem ? selectedItem.gtin13 : null,
        mstItemGtin14: selectedItem ? selectedItem.gtin14 : null,
        mstItemItf: selectedItem ? selectedItem.itf : null,
        mstItemSdp: selectedItem ? selectedItem.sdp : null,
        mstItemReplaceOrgItemCode: selectedItem ? selectedItem.replaceOrgItemCode : null,
        mstItemMakerGln: selectedItem ? selectedItem.makerGln : null,
        mstItemMakerName: selectedItem ? selectedItem.makerName : null,
        mstItemOrgSupplierId: selectedItem ? selectedItem.supplierName : null,
        mstItemMinOrderQuantity: selectedItem ? selectedItem.minOrderQuantity : null,
        mstItemOrderUnitType: selectedItem ? selectedItem.orderUnitType : null,
        mstItemCaseLot: selectedItem ? selectedItem.caseLot : null,
        mstItemIsDiscontinued: selectedItem ? selectedItem.isDiscontinued : null,
        mstItemDeleteFlg: selectedItem ? selectedItem.deleteFlg : null,
      })
    }
  }, [items])

  // 入力チェック
  const fieldValidate = (pRow: MstCenterStockItemWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    // console.log("fieldValidate", field, pRow)
    switch (field) {
      case 'orgItemCode':
      case 'orgCenterId':
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break
      default:
        return false
    }
    return hasErrors
  }

  useEffect(() => {
    // 値変更時に、useStateの値を参照する処理を行う場合
    // まとめてコピペした場合など、1回しか発生しないので、変更行はまとめて設定する
    if (!rowChangeTriggerRowID) return
    // console.log("cellChangeTrigger", rowChangeTriggerRowID, items.length)
    const updatedRows: MstCenterStockItemWithId[] = [];
    gridRef.current?.api?.forEachNode((node) => {
      if (node?.data) {
        const trRow = node.data as MstCenterStockItemWithId
        // console.log("cellChangeTrigger",trRow.isEdited, `'${trRow.orgCenterId}'`,searchParam.orgItemCode)
        if (trRow.isEdited) {
          // 新規行にセンターIDが入っていない場合は、検索条件のセンターIDをセット
          if (!trRow.orgCenterId) {
            const searchCenterDefault = center.find((rowCenter) => rowCenter.centerId === searchParam.centerId)
            trRow.orgCenterId = searchCenterDefault?.orgCenterId || null
            if (trRow.orgItemCode) duplicateCheck(trRow, trRow.orgCenterId)
          }
          // 商品マスタの値を設定
          setMstItemCol(trRow, trRow.orgItemCode)
          // node.setData(trRow)
          updatedRows.push(trRow)
        }
      }
    })
    if (updatedRows.length > 0) gridRef.current?.api?.applyTransaction({ update: updatedRows })
    // 必要な処理が終わったら、NULLに戻すこと！
    setRowChangeTriggerRowID(null);
  }, [rowChangeTriggerRowID])

  const cellValueChanged = useCallback((event: CellValueChangedEvent<MstCenterStockItemWithId>, hasError: boolean) => {
    const { data, colDef, newValue, oldValue, node } = event
    const { field } = colDef
    // なぜか以下のconsole.logをコメントアウトすると、新規行にセンターID設定がうまく動かない・・・
    // eslint-disable-next-line no-console
    console.log('cellValueChanged', field, newValue, oldValue, data, hasError, items.length)
    const updatedRow = { ...data }
    if (field === 'orgItemCode') {
      // カスタムフックから直接コンポーネントの状態にアクセスすることはできないため、ここでsetMstItemColの処理をした場合、itemsが取れない
      // setMstItemCol(updatedRow, newValue)
      // node.setData(updatedRow)
      // setRowChangeTriggerRowID(updatedRow.id)
    } else if (field === "orgSupplierId") {
      // 仕入先コード変更時に、APIのエラーを解消
      checkAndSetError(updatedRow, "orgCenterId", null);
      checkAndSetError(updatedRow, "orgItemCode", null);
      // API以外のエラーを再度チェック
      fieldValidate(updatedRow, "orgCenterId", updatedRow.orgCenterId)
      fieldValidate(updatedRow, "orgItemCode", updatedRow.orgItemCode)
    }
    // useGridEdit の editing が true のまま残るケースでも親の 登録 有効化と同期する（ItemInfoDisplay 等）
    if (event.data?.isEdited) {
      setHasGridEdited(true)
    }
  }, [items, setHasGridEdited])

  const alertIconCellRenderer = useCallback((params: any) => {
    const hasAlert = params.value === true
    if (!hasAlert) {
      return null
    }

    return (
      <span className="action-icon-info has_alert">
        <AnnouncementIcon fontSize="small" />
      </span>
    )
  }, [])

  const updateMstCenterStockItem = useCallback(
    async (updatedRows: MstCenterStockItemWithId[]) => {
      const datalist = updatedRows.map(({ isEdited, editedFields, startDate, salesEndAlertDate, errors, ...rest }) => ({
        ...rest,
        startDate: startDate ? convertDateToRequestParam(new Date(startDate)) : null,
        salesEndAlertDate: salesEndAlertDate ? convertDateToRequestParam(new Date(salesEndAlertDate)) : null,
      }))

      try {
        await fetchCenterStockItemUpdate({
          mstCenterStockItemUpdate: { body: datalist },
        })
        if (onReload) onReload()
      } catch (err: any) {
        if (err?.errors && err.result?.status === 'error') {
          const gridApi = gridRef.current?.api

          err.errors.forEach((errrow: any) => {
            const { rowdata } = errrow
            const orgCenterId = (rowdata as { org_center_id: string | null })?.org_center_id
            const orgItemCode = (rowdata as { org_item_code: string | null })?.org_item_code
            const errRow = updatedRows.find(
              (rowdatas) => rowdatas.orgCenterId === orgCenterId && rowdatas.orgItemCode === orgItemCode
            )
            if (errRow) {
              // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
              errRow.errors = {
                orgCenterId: [errrow.errorMessage],
                orgItemCode: [errrow.errorMessage],
              }
              const rowNode = gridApi?.getRowNode(errRow.id || '')
              if (rowNode) {
                rowNode.setData(errRow)
              }
            }
          })
        }
      }
    },
    [fetchCenterStockItemUpdate, searchParam]
  )

  const handleImportExcel = (data: any[]) => {
    let hasError = false
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstCenterStockItemWithId> = {}

      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping]
          const importvalue = excelRow[key]
          let setvalue = importvalue || null
          if (['deleteFlg', 'stopShip', 'stopArrival', 'isMuted', 'isDiscontinued'].indexOf(tableField) >= 0) {
            setvalue = parseBoolean(excelRow[key])
          } else if (['orderUnitType'].indexOf(tableField) >= 0) {
            const group = tableFieldToGroup[tableField]
            const typeList = orderSysTypeCodeLists[group]

            const codevalue = typeCodeParser(importvalue, typeList)
            setvalue = typeCodeParser(codevalue, typeList)
            if (!codevalue && importvalue) hasError = true
          } else if (tableField === 'startDate' || tableField === 'salesEndAlertDate') {
            setvalue = parseDate(importvalue) as unknown as undefined
          } else if (['caseLot'].indexOf(tableField) >= 0) {
            setvalue = parsePositiveInteger(importvalue)
          } else if (['minOrderQuantity', 'stockDaysSetting', 'minStockQuantity'].indexOf(tableField) >= 0) {
            setvalue = parsePositiveIntegerZero(importvalue)
          } else if (['safetyStockSetting'].indexOf(tableField) >= 0) {
            setvalue = parseNumeric(importvalue)
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
          mappedRow[tableField as keyof MstCenterStockItemWithId] = setvalue as unknown as undefined
        }
      })

      const { orgItemCode, orgCenterId } = mappedRow
      setMstItemCol(mappedRow as MstCenterStockItemWithId, orgItemCode)

      return mappedRow as MstCenterStockItemWithId
    })
    return {mappedData, hasError}
  }

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    parseStringValue,
    parseTime,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    dateTreeSetFilterParams,
    clearPinnedRows,
    createCellClassRules,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    updateData: updateMstCenterStockItem,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    cellValueChanged,
    setRowChangeTriggerRowID,  
    storageKey: 'gridColumnState:/master-stock-center-item',
    // scrollPositionKey: 'orgItemCode',
    // rowData: row,
  })

  const unmatchLotCellClassRules = useMemo(
    () => createCellClassRules('unmatchLot', true, 'alert_middle'),
    [createCellClassRules]
  )

  const unmatchRenewalCellClassRules = useMemo(
    () => createCellClassRules('unmatchRenewal', true, 'alert_middle'),
    [createCellClassRules]
  )

  const unmatchStockdaysCellClassRules = useMemo(
    () => createCellClassRules('unmatchStockdays', true, 'alert_middle'),
    [createCellClassRules]
  )

  const inactiveCellClassRules = useMemo(
    () => createCellClassRules('isInactive', true, 'alert_middle'),
    [createCellClassRules]
  )

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef)[] => [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: 'アラート',
      field: 'isAlert',
      pinned: 'left',
      width: 70,
      cellClass: 'boolean-cell',
      cellRenderer: alertIconCellRenderer,
      tooltipValueGetter: (p) => p.data?.alertTypeName ?? '',
    },
    {
      headerName: '自社センター',
      field: 'orgCenterId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      width: 300,
      suppressPaste: false,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
      selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgCenterId, center, "orgCenterId", "centerName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, center, 'orgCenterId'),
      pinned: 'left',
      // sort: 'asc',
      // sortIndex: 0,
      cellEditorParams: editorDataReady
        ? ({
            values: [null, ...centerSelect],
            formatValue: (value: string) => selectValueFormatter(value, center, 'orgCenterId', 'centerName'),
            searchType: 'matchAny',
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          } as IRichCellEditorParams)
        : { values: ['-'] },
    },
    {
      headerName: '社内商品コード',
      field: 'orgItemCode',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      width: 300,
      suppressPaste: false,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        selectValueFormatter(params.value, items, 'orgItemCode', 'itemName'),
      valueParser: (params: ValueParserParams) => selectValueParser(params, items, 'orgItemCode'),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgItemCode, items, "orgItemCode", "itemName"),
      pinned: 'left',
      // sort: 'asc',
      // sortIndex: 1,
      cellEditorParams: editorDataReady
        ? ({
            values: [null, ...itemSelect],
            formatValue: (value: string) => selectValueFormatter(value, items, 'orgItemCode', 'itemName'),
            searchType: 'matchAny',
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          } as IRichCellEditorParams)
        : { values: ['-'] },
    },
    {
      headerName: '商品マスタ',
      children: [
        {
          headerName: '商品名称',
          field: 'mstItemItemName',
          width: 250,
        },
        {
          headerName: '発売日',
          field: 'mstItemSalesStartdate', // 発売日
          cellClass: 'date-cell',
          filterParams: {
            filters: [
              {filter: 'agDateColumnFilter'},
              {filter: 'agSetColumnFilter',
               filterParams: dateTreeSetFilterParams,
              },
            ],
            closeOnApply: true,
          },
          width: 150,
          valueFormatter: dateFormatter,
          valueParser: parseDateValue,
        },
        {
          headerName: '終了日',
          field: 'mstItemSalesEnddate', // 終了日
          cellClass: 'date-cell',
          filter: 'agMultiColumnFilter',
          filterParams: {
            filters: [
              {filter: 'agDateColumnFilter'},
              {filter: 'agSetColumnFilter',
               filterParams: dateTreeSetFilterParams,
              },
            ],
            closeOnApply: true,
          },
          width: 150,
          columnGroupShow: 'open',
          valueFormatter: dateFormatter,
          valueParser: parseDateValue,
        },
        {
          headerName: 'ケースサイズ 長手（mm）',
          field: 'mstItemLongside', // ケースサイズ 長手（mm）
          width: 200,
          columnGroupShow: 'open',
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
        },
        {
          headerName: 'ケースサイズ 短手（mm）',
          field: 'mstItemShortside', // ケースサイズ 短手（mm）
          width: 200,
          columnGroupShow: 'open',
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
        },
        {
          headerName: 'ケースサイズ 高さ（mm）',
          field: 'mstItemHeight', // ケースサイズ 高さ（mm）
          width: 200,
          columnGroupShow: 'open',
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
        },
        {
          headerName: 'ケースサイズ 重量（g）',
          field: 'mstItemWeight', // ケースサイズ 重量（g）
          width: 200,
          columnGroupShow: 'open',
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
        },
        {
          headerName: '規格',
          field: 'mstItemSpec',
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: 'パレット最大積付数',
          field: 'mstItemMaxPalletQuantity', // パレット最大積付数
          width: 200,
          columnGroupShow: 'open',
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
        },
        {
          headerName: 'パレット積載数（面）',
          field: 'mstItemPalletQuantityFace', // パレット積載数（面）
          width: 200,
          columnGroupShow: 'open',
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: numberValueFormatter,
        },
        {
          headerName: 'パレット積載数（段）',
          field: 'mstItemPalletQuantityTier', // パレット積載数（段）
          width: 200,
          columnGroupShow: 'open',
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: numberValueFormatter,
        },
        {
          headerName: 'JANコード（バラ）',
          field: 'mstItemJan', // JANコード（バラ）
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: 'JANコード（ケース）',
          field: 'mstItemJanCase', // JANコード（ケース）
          width: 200,
        },
        {
          headerName: 'GTINコード（バラ）',
          field: 'mstItemGtin13', // GTINコード（バラ）
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: 'GTINコード（ケース）',
          field: 'mstItemGtin14', // GTINコード（ケース）
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: 'ITFコード',
          field: 'mstItemItf', // ITFコード
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: 'SDPコード',
          field: 'mstItemSdp', // SDPコード
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: '後継社内商品',
          field: 'mstItemReplaceOrgItemCode', // 後継社内商品
          width: 200,
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) =>
            selectValueFormatter(params.value, items, 'mstItemReplaceOrgItemCode', 'itemName'),
        },
        {
          headerName: 'メーカーGS1事業者コード',
          field: 'mstItemMakerGln', // メーカーGS1事業者コード
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: 'メーカー名',
          field: 'mstItemMakerName', // メーカー名
          width: 200,
          columnGroupShow: 'open',
        },
        {
          headerName: '仕入先',
          field: 'mstItemOrgSupplierId', // 仕入先
          width: 220,
          // valueFormatter: (params: ValueFormatterParams) => selectValueFormatter(params.value, supplier, "mstItemOrgSupplierId", "supplierName"),
        },
        {
          headerName: '発注単位',
          field: 'mstItemOrderUnitType', // 発注単位
          width: 200,
          valueFormatter: (params: ValueFormatterParams) =>
            typecodeFormatter(params.value, orderSysTypeCodeLists['order_unit_type'!]),
          filterValueGetter: (params) => typecodeFormatter(params.data.mstItemOrderUnitType, orderSysTypeCodeLists['order_unit_type'!]),
        },
        {
          headerName: 'ケースロット数',
          field: 'mstItemCaseLot', // ケースロット数
          width: 200,
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: numberValueFormatter,
        },
        {
          headerName: '最低発注量',
          field: 'mstItemMinOrderQuantity', // 最低発注量
          width: 200,
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: numberValueFormatter,
        },
        {
          headerName: '取扱終了',
          field: 'mstItemIsDiscontinued', // 取扱終了
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          columnGroupShow: 'open',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '削除',
          field: 'mstItemDeleteFlg', // 削除
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          columnGroupShow: 'open',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
      ],
    },
    {
      headerName: 'センター別発注設定',
      children: [
        {
          headerName: '発注単位',
          field: 'orderUnitType',
          editable: isEnableEdit(),
          width: 200,
          suppressPaste: false,
          cellEditor: 'agRichSelectCellEditor',
          valueFormatter: (params: ValueFormatterParams) =>
            typecodeFormatter(params.value, orderSysTypeCodeLists['order_unit_type'!]),
          valueParser: (params: ValueParserParams) =>
            selectTypecodeValueParser(params, orderSysTypeCodeLists['order_unit_type'!]),
          filterValueGetter: (params) => typecodeFormatter(params.data.orderUnitType, orderSysTypeCodeLists['order_unit_type'!]),
          cellEditorParams: editorDataReady
            ? ({
                values: [null, ...orderSysTypeCodeSelects['order_unit_type'!]],
                formatValue: (value: string) =>
                  selectValueFormatter(value, orderSysTypeCodeLists['order_unit_type'!], 'typeCode', 'typeName'),
              } as IRichCellEditorParams)
            : { values: ['-'] },
        },
        {
          headerName: 'ケースロット数',
          field: 'caseLot',
          editable: isEnableEdit(),
          width: 200,
          cellClass: 'number-cell',
          cellClassRules: unmatchLotCellClassRules,
          filter: 'agNumberColumnFilter',
          cellEditor: 'agNumberCellEditor',
          // cellEditorParams: {
          //   step: 1,
          //   min: 0,
          // },
          valueParser: numberValueParser,
          valueFormatter: numberValueFormatter,
        },
        {
          headerName: '最低発注量',
          field: 'minOrderQuantity',
          editable: isEnableEdit(),
          width: 200,
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          cellEditor: 'agNumberCellEditor',
          // cellEditorParams: {
          //   step: 1,
          //   min: 0,
          // },
          valueParser: numberValueParserZero,
          valueFormatter: numberValueFormatter,
        },
        {
          headerName: '出荷停止',
          field: 'stopShip',
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '入荷停止',
          field: 'stopArrival',
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '仕入先',
          field: 'orgSupplierId',
          editable: isEnableEdit(),
          width: 220,
          suppressPaste: false,
          cellEditor: 'agRichSelectCellEditor',
          valueFormatter: (params: ValueFormatterParams) =>
            selectValueFormatter(params.value, supplier, 'orgSupplierId', 'supplierName'),
          filterValueGetter: (params) => selectValueFormatter(params.data.orgSupplierId, supplier, "orgSupplierId", "supplierName"),
          valueParser: (params: ValueParserParams) => selectValueParser(params, supplier, 'orgSupplierId'),
          cellEditorParams: editorDataReady
            ? ({
                values: [null, ...supplierSelect],
                formatValue: (value: string) => selectValueFormatter(value, supplier, 'orgSupplierId', 'supplierName'),
                searchType: 'matchAny',
                allowTyping: true,
                filterList: true,
                highlightMatch: true,
              } as IRichCellEditorParams)
            : { values: ['-'] },
        },
      ],
    },
    {
      headerName: '在庫設定',
      children: [
        {
          headerName: '適用開始日',
          field: 'startDate',
          editable: isEnableEdit(),
          cellClassRules: unmatchRenewalCellClassRules,
          width: 130,
          minWidth: 130,
          maxWidth: 250,
          filterParams: {
            filters: [
              {filter: 'agDateColumnFilter'},
              {filter: 'agSetColumnFilter',
               filterParams: dateTreeSetFilterParams,
              },
            ],
            closeOnApply: true,
          },
          cellEditor: 'agDateCellEditor',
          valueFormatter: dateFormatter,
          valueParser: parseDateValue,
        },
        {
          headerName: '下限在庫日数',
          field: 'stockDaysSetting',
          editable: isEnableEdit(),
          width: 200,
          cellClass: 'number-cell',
          cellClassRules: unmatchStockdaysCellClassRules,
          filter: 'agNumberColumnFilter',
          cellEditor: 'agNumberCellEditor',
          // cellEditorParams: {
          //   step: 1,
          //   min: 0,
          // },
          valueParser: numberValueParserZero,
          valueFormatter: numberValueFormatter,
        },
        {
          headerName: '欠品許容率',
          field: 'safetyStockSetting',
          editable: isEnableEdit(),
          width: 200,
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          cellEditor: 'agNumberCellEditor',
          // cellEditorParams: {
          //   step: 1e-18,
          //   min: 0,
          // },
          valueFormatter: (params) => formatDecimalValue(params, 3),
          valueParser: (params) => parseDecimalValue(params, 3),
        },
        {
          headerName: '最低在庫数',
          field: 'minStockQuantity',
          editable: isEnableEdit(),
          width: 200,
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          cellEditor: 'agNumberCellEditor',
          // cellEditorParams: {
          //   step: 1,
          //   min: 0,
          // },
          valueParser: numberValueParserZero,
          valueFormatter: numberValueFormatter,
        },
      ],
    },
    {
      headerName: '管理項目',
      children: [
        {
          headerName: '終売アラート開始日',
          field: 'salesEndAlertDate',
          editable: isEnableEdit(),
          width: 200,
          filter: 'agDateColumnFilter',
          cellEditor: 'agDateCellEditor',
          valueFormatter: dateFormatter,
          valueParser: parseDateValue,
        },
        {
          headerName: '通知停止',
          field: 'isMuted', // 通知停止
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '取扱終了',
          field: 'isDiscontinued', // 取扱終了
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '削除',
          field: 'deleteFlg', // 削除
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue,
        },
        {
          headerName: '備考',
          field: 'remarks', 
          editable: isEnableEdit(),
          width: 200,
          cellEditorPopup: true,
          cellEditor: 'agLargeTextCellEditor',
          cellEditorParams: longTextEditorParams,
          valueFormatter: longTextValueFormatter,
          valueParser: longTextValueParser,
          valueSetter: longTextValueSetter,
        },
      ],
    },
    {
      headerName: 'PSI計算項目',
      children: [
        {
          headerName: '商品コード',
          field: 'itemCode',
          width: 200,
        },
        {
          headerName: '最新商品',
          field: 'recencyFlg',
          cellRenderer: 'agCheckboxCellRenderer',
          width: 200,
        },
        {
          headerName: 'センター取り扱い商品',
          field: 'centerTargetItemFlg',
          cellRenderer: 'agCheckboxCellRenderer',
          width: 200,
        },
        {
          headerName: '適用終了日',
          field: 'endDate', // 適用終了日
          width: 200,
          cellClassRules: unmatchRenewalCellClassRules,
          valueParser: parseDateValue,
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          headerName: '前の社内商品コード',
          field: 'prevOrgItemCode', // 前の社内商品コード
          width: 200,
          cellClassRules: unmatchRenewalCellClassRules,
        },
        {
          headerName: '次の社内商品コード',
          field: 'nextOrgItemCode', // 次の社内商品コード
          width: 200,
          cellClassRules: unmatchRenewalCellClassRules,
        },
        {
          headerName: '最終入荷日',
          field: 'lastArrivalDate', // 最終入荷日
          width: 200,
          cellClassRules: inactiveCellClassRules,
          valueParser: parseDateValue,
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          headerName: '最終出荷日',
          field: 'lastShipDate', // 最終出荷日
          width: 200,
          cellClassRules: inactiveCellClassRules,
          valueParser: parseDateValue,
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          headerName: '最新在庫数',
          field: 'lastStockQuantity', // 最新在庫数
          width: 200,
          valueFormatter: numberValueFormatter,
          cellClass:'number-cell',
          cellClassRules: inactiveCellClassRules,
          filter: 'agNumberColumnFilter',
        },
      ],
    },
  ]
  const columnDefs = useMemo<ColDef[]>(updateColumnDefs, [center, editorDataReady])

  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
          { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
          { headerName: '自社センター', children: [{ headerName: 'org_center_id', field: 'orgCenterId', cellClass: 'stringType' }] },
          { headerName: '社内商品コード', children: [{ headerName: 'org_item_code', field: 'orgItemCode', cellClass: 'stringType' }] },
          { headerName: '適用開始日', children: [{ headerName: 'start_date', field: 'startDate', valueFormatter: dateFormatter }] },
          { headerName: '下限在庫日数', children: [{ headerName: 'stock_days_setting', field: 'stockDaysSetting', cellClass: 'numberType' }] },
          { headerName: '欠品許容率', children: [{ headerName: 'safety_stock_setting', field: 'safetyStockSetting', cellClass: 'decimalType' }] },
          { headerName: '最低在庫数', children: [{ headerName: 'min_stock_quantity', field: 'minStockQuantity', cellClass: 'numberType' }] },
          { headerName: '出荷停止', children: [{ headerName: 'stop_ship', field: 'stopShip', cellClass: 'booleanType' }] },
          { headerName: '入荷停止', children: [{ headerName: 'stop_arrival', field: 'stopArrival', cellClass: 'booleanType' }] },
          { headerName: '仕入先', children: [{ headerName: 'org_supplier_id', field: 'orgSupplierId', cellClass: 'stringType' }] },
          { headerName: '発注単位', children: [{ headerName: 'order_unit_type', field: 'orderUnitType', cellClass: 'stringType' }] },
          { headerName: 'ケースロット数', children: [{ headerName: 'case_lot', field: 'caseLot', cellClass: 'numberType' }] },
          { headerName: '最低発注量', children: [{ headerName: 'min_order_quantity', field: 'minOrderQuantity', cellClass: 'numberType' }] },
          { headerName: '終売アラート開始日', children: [{ headerName: 'sales_end_alert_date', field: 'salesEndAlertDate', valueFormatter: dateFormatter }] },
          { headerName: '通知停止', children: [{ headerName: 'is_muted', field: 'isMuted', cellClass: 'booleanType' }] },
          { headerName: '取扱終了', children: [{ headerName: 'is_discontinued', field: 'isDiscontinued', cellClass: 'booleanType' }] },
          { headerName: '削除', children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }] },
          { headerName: '商品コード', children: [{ headerName: 'item_code', field: 'itemCode', cellClass: 'stringType' }] },
          { headerName: '最新商品', children: [{ headerName: 'recency_flg', field: 'recencyFlg', cellClass: 'booleanType' }] },
          { headerName: 'センター取り扱い商品', children: [{ headerName: 'center_target_item_flg', field: 'centerTargetItemFlg', cellClass: 'booleanType' }] },
          { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
          { headerName: '適用終了日', children: [{ headerName: 'end_date', field: 'endDate', valueFormatter: dateFormatter }] },
          { headerName: '前の社内商品コード', children: [{ headerName: 'prev_org_item_code', field: 'prevOrgItemCode', cellClass: 'stringType' }] },
          { headerName: '次の社内商品コード', children: [{ headerName: 'next_org_item_code', field: 'nextOrgItemCode', cellClass: 'stringType' }] },
          { headerName: '最終入荷日', children: [{ headerName: 'last_arrival_date', field: 'lastArrivalDate', valueFormatter: dateFormatter }] },
          { headerName: '最終出荷日', children: [{ headerName: 'last_ship_date', field: 'lastShipDate', valueFormatter: dateFormatter }] },
          { headerName: '最新在庫数', children: [{ headerName: 'last_stock_quantity', field: 'lastStockQuantity', cellClass: 'numberType' }] },
        ])
    },[setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow()
      setRow([blankRow])
    } else {
      const datalist = list.list.map((item) => ({ ...item, ...initRecordEditEx() }))
      datalist.forEach((item) => {
        setMstItemCol(item as MstCenterStockItemWithId, item.orgItemCode)
      })
      setRow(datalist)
      clearPinnedRows()
    }
  }, [list.list, createBlankRow, clearPinnedRows, setMstItemCol])

  useEffect(() => {
    if (!dialogEdits?.length) return

    let adds: MstCenterStockItemWithId[] = []
    let updates: MstCenterStockItemWithId[] = []

    setRow((prev) => {
      const dialogApplyResult = applyCenterStockDialogEdits(prev, dialogEdits, createBlankRow, setMstItemCol)
      adds = dialogApplyResult.adds
      updates = dialogApplyResult.updates
      return dialogApplyResult.next
    })

    if (adds.length === 0 && updates.length === 0) return

    setHasGridEdited(true)
    syncDialogEditsToAgGrid(gridRef, adds, updates)
  }, [createBlankRow, dialogEdits, gridRef, setHasGridEdited, setMstItemCol])

  useEffect(() => {
    if (!focusTarget?.orgCenterId || !focusTarget?.orgItemCode) return
    const gridApi = gridRef.current?.api
    if (!gridApi) return
    const matchCenter = String(focusTarget.orgCenterId).trim()
    const matchItem = String(focusTarget.orgItemCode).trim()
    const run = () => {
      gridApi.deselectAll()
      gridApi.forEachNode((node) => {
        const centerId = String(node.data?.orgCenterId ?? '').trim()
        const itemCode = String(node.data?.orgItemCode ?? '').trim()
        if (
          centerId !== matchCenter ||
          itemCode !== matchItem ||
          node.rowIndex == null
        ) {
          return
        }
        gridApi.ensureIndexVisible(node.rowIndex, 'middle')
        node.setSelected(true, true)
      })
      onFocusHandled?.()
    }
    requestAnimationFrame(() => requestAnimationFrame(run))
  }, [focusTarget, gridRef, onFocusHandled])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            key={`${row[0]?.companyId}-${editorDataReady}`}
            rowData={row}
            ref={gridRef}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            autoSizeStrategy={autoSizeStrategy}
            loadingOverlayComponent={LoadingOverlay}
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
            suppressColumnVirtualisation
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
